import React from "react";
import cls from "./storeContainer.module.scss";
import { Category, IShop } from "interfaces";
import { Member, ShopProvider } from "contexts/shop/shop.provider";
import { useMediaQuery } from "@mui/material";
import dynamic from "next/dynamic";
import Message3FillIcon from "remixicon-react/Message3FillIcon";
import useModal from "hooks/useModal";
import { useAppDispatch, useAppSelector } from "hooks/useRedux";
import { toggleCartButton } from "redux/slices/carts";
import ShoppingCart2FillIcon from "remixicon-react/ShoppingCart2FillIcon";
import { useTranslation } from "react-i18next";

const DrawerContainer = dynamic(() => import("containers/drawer/drawer"));
const Sidebar = dynamic(() => import("containers/sidebar/sidebar"));
const Chat = dynamic(() => import("containers/chat/chat"));
const MobileSearchContainer = dynamic(
  () => import("containers/mobileSearchContainer/mobileSearchContainer"),
);

type Props = {
  data?: IShop;
  children: any;
  memberState: Member;
  categories: Category[];
};

export default function StoreContainer({
  data,
  children,
  memberState,
  categories,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { list } = useAppSelector((state) => state.carts);
  const isDesktop = useMediaQuery("(min-width: 1140px)");
  const isMobile = useMediaQuery("(max-width: 700px)");
  const [open, handleOpen, handleClose] = useModal();
  const [searchModal, handleOpenSearchModal, handleCloseSearchModal] =
    useModal();

  return (
    <ShopProvider memberState={memberState} data={data}>
      <div className={cls.container}>
        <div className="container">
          <div className={cls.wrapper}>
            {isDesktop && (
              <>
                <div className={cls.sidebar}>
                  <Sidebar data={categories} />
                </div>
                <div className={cls.line} />
              </>
            )}
            <div className={cls.main}>
              {!isDesktop && (
                <button
                  type="button"
                  className={cls.search}
                  onClick={handleOpenSearchModal}
                >
                  <span>{`${t("search.in")} ${t("this.shop")}`}</span>
                </button>
              )}
              {React.Children.map(children, (child) => {
                return child
                  ? React.cloneElement(child, {
                      data,
                      categories,
                      handleOpenChat: handleOpen,
                      handleCloseChat: handleClose,
                    })
                  : null;
              })}
            </div>
          </div>
        </div>
        {isMobile ? (
          <button
            type="button"
            className={cls.actionBtn}
            onClick={() => dispatch(toggleCartButton())}
          >
            <ShoppingCart2FillIcon size={24} />
            {!!list.length && <div className={cls.indicator} />}
          </button>
        ) : (
          <button type="button" className={cls.actionBtn} onClick={handleOpen}>
            <Message3FillIcon size={24} />
          </button>
        )}
      </div>
      <DrawerContainer
        open={open}
        onClose={handleClose}
        PaperProps={{ style: { padding: 0, width: "500px" } }}
      >
        <Chat
          receiverId={data?.user_id}
          title={data?.translation?.title || "shop"}
          translateTitle={!data?.translation?.title}
        />
      </DrawerContainer>
      <MobileSearchContainer
        open={searchModal}
        onClose={handleCloseSearchModal}
        fullScreen
      />
    </ShopProvider>
  );
}
